import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';

const MIN_MONTH = '2026-06';

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function listMonthsFrom(minMonth) {
  const out = [];
  let cur = minMonth;
  const now = new Date();
  const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  while (cur <= end) {
    out.push(cur);
    cur = shiftMonth(cur, 1);
  }
  return out.reverse();
}

function monthLabel(month) {
  const [y, m] = String(month || '').split('-').map(Number);
  if (!y || !m) return month;
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

function presentWords(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function tidyActivity(raw) {
  return presentWords(String(raw || '').replace(/^\d+\.\s*/, ''));
}

function parseLessonRest(rest) {
  let batch = '';
  let body = rest;
  const splitAt = body.indexOf(' · ');
  if (splitAt > 0) {
    batch = body.slice(0, splitAt).trim();
    body = body.slice(splitAt + 3).trim();
  }
  if (body.startsWith('Note:')) {
    return { kind: 'note', batch, text: body.slice(5).trim() };
  }
  if (body.startsWith('Attended.')) {
    return { kind: 'empty', batch, text: 'No lesson notes for this class.' };
  }
  const levelMatch = body.match(/^(.*?) \(([^)]+)\)(?:: (.*))?$/);
  if (levelMatch) {
    return {
      kind: 'lesson',
      batch,
      lesson: presentWords(levelMatch[1]),
      level: presentWords(levelMatch[2]),
      activities: levelMatch[3] ? levelMatch[3].split(/,\s+/).map(tidyActivity).filter(Boolean) : [],
    };
  }
  const colon = body.indexOf(': ');
  if (colon > 0) {
    return {
      kind: 'lesson',
      batch,
      lesson: presentWords(body.slice(0, colon)),
      level: '',
      activities: body.slice(colon + 2).split(/,\s+/).map(tidyActivity).filter(Boolean),
    };
  }
  return { kind: 'lesson', batch, lesson: presentWords(body), level: '', activities: [] };
}

function parseTaughtText(text) {
  const groups = [];
  const byDate = new Map();
  const free = [];
  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const match = line.match(/^(\d{1,2} [A-Za-z]{3}) · (.+)$/);
      if (!match) {
        free.push(line);
        return;
      }
      const date = match[1];
      if (!byDate.has(date)) {
        const group = { date, items: [] };
        byDate.set(date, group);
        groups.push(group);
      }
      byDate.get(date).items.push(parseLessonRest(match[2]));
    });
  return { groups, free };
}

function TaughtNoteView({ text }) {
  const { groups, free } = parseTaughtText(text);
  if (!groups.length && !free.length) {
    return <Typography color="text.secondary">Nothing has been written for this student yet.</Typography>;
  }
  return (
    <Stack spacing={2}>
      {groups.map((group) => (
        <Box key={group.date}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
            {group.date}
          </Typography>
          <Stack spacing={1}>
            {group.items.map((item, index) => {
              if (item.kind === 'note' || item.kind === 'empty') {
                return (
                  <Typography key={`${group.date}-${index}`} variant="body2" color="text.secondary">
                    {item.batch ? `${item.batch} · ` : ''}
                    {item.text}
                  </Typography>
                );
              }
              return (
                <Box
                  key={`${group.date}-${item.lesson}-${index}`}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography fontWeight={700}>{item.lesson}</Typography>
                    {item.level ? <Chip size="small" label={item.level} /> : null}
                    {item.batch ? <Chip size="small" variant="outlined" label={item.batch} /> : null}
                  </Stack>
                  {item.activities.length > 0 ? (
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                      {item.activities.map((activity, activityIndex) => (
                        <Chip key={`${activity}-${activityIndex}`} size="small" variant="outlined" label={activity} />
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
      {free.length > 0 ? (
        <Box>
          <Typography variant="overline" color="text.secondary">
            Other
          </Typography>
          {free.map((line) => (
            <Typography key={line} variant="body2" sx={{ mb: 0.5 }}>
              {line}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Stack>
  );
}

function StudentNoteEditor({ month, studentId, onDirtyChange }) {
  const queryClient = useQueryClient();
  const noteQuery = useQuery(
    ['studentTaughtNote', month, studentId],
    async () => {
      const res = await teacherModuleAPI.getStudentTaughtNote(month, studentId);
      return res.data?.data;
    },
    { enabled: !!month && !!studentId }
  );
  const note = noteQuery.data;
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setText(note?.text || '');
  }, [note?.studentId, note?.updatedAt, note?.text]);

  const saveMutation = useMutation(
    () => teacherModuleAPI.saveStudentTaughtNote(month, { studentId, text }),
    {
      onSuccess: () => {
        toast.success(`Saved for ${note?.studentName || 'student'}`);
        setEditing(false);
        queryClient.invalidateQueries(['studentTaughtNote', month, studentId]);
        queryClient.invalidateQueries(['studentTaughtNotes', month]);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not save'),
    }
  );

  const dirty = !!note && text !== (note.text || '');

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  if (noteQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (noteQuery.isError || !note) {
    return (
      <Alert severity="error">
        {noteQuery.error?.response?.data?.message || 'Could not load this student.'}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {note.studentName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(note.batches || []).join(', ')} · {note.classCount} class{note.classCount === 1 ? '' : 'es'} attended
          </Typography>
        </Box>
        <Chip
          size="small"
          color={note.saved ? 'success' : 'default'}
          variant={note.saved ? 'filled' : 'outlined'}
          label={note.saved ? 'Saved note' : 'From class notes'}
        />
      </Stack>
      {editing ? (
        <TextField
          fullWidth
          multiline
          minRows={10}
          maxRows={18}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What this student was taught"
          inputProps={{ 'aria-label': `Note for ${note.studentName}` }}
          sx={{ bgcolor: 'grey.50' }}
        />
      ) : (
        <Box sx={{ maxHeight: 520, overflow: 'auto', pr: 0.5 }}>
          <TaughtNoteView text={text} />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Classes they attended with you in {monthLabel(month)}. Saving does not change the class log.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
        {dirty ? (
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={saveMutation.isLoading}>
            Save
          </Button>
        ) : null}
        <Button variant="outlined" onClick={() => setEditing((open) => !open)}>
          {editing ? 'Show lessons' : 'Edit wording'}
        </Button>
        <Button
          variant="text"
          onClick={() => setText(note.generatedText || '')}
          disabled={text === (note.generatedText || '')}
        >
          Start again from class notes
        </Button>
        {editing && dirty ? (
          <Button
            variant="text"
            onClick={() => {
              setText(note.text || '');
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function TeacherStudentTaught() {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthOptions = useMemo(() => listMonthsFrom(MIN_MONTH), []);
  const [month, setMonth] = useState(searchParams.get('month') || monthOptions[0] || MIN_MONTH);
  const [batchId, setBatchId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [studentId, setStudentId] = useState('');
  const [dirtyGuard, setDirtyGuard] = useState(false);

  useEffect(() => {
    setSearchParams({ month }, { replace: true });
  }, [month, setSearchParams]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const canList = Boolean(batchId) || search.length >= 2;

  const listQuery = useQuery(
    ['studentTaughtNotes', month, batchId, search],
    async () => {
      const params = {};
      if (batchId) params.batchId = batchId;
      if (search.length >= 2) params.q = search;
      const res = await teacherModuleAPI.getStudentTaughtNotes(month, params);
      return res.data?.data;
    },
    { enabled: !!month }
  );

  const batches = listQuery.data?.batches || [];
  const students = listQuery.data?.students || [];
  const totalStudents = listQuery.data?.totalStudents || 0;

  const chooseStudent = (nextId) => {
    if (nextId === studentId) return;
    if (dirtyGuard && !window.confirm('You have unsaved changes. Leave this student without saving?')) return;
    setDirtyGuard(false);
    setStudentId(nextId);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        What each student was taught
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Pick a batch or search for a student, then edit that one note.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="Month"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              setStudentId('');
              setDirtyGuard(false);
            }}
            sx={{ minWidth: 180 }}
          >
            {monthOptions.map((m) => (
              <MenuItem key={m} value={m}>
                {monthLabel(m)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Batch"
            value={batchId}
            onChange={(e) => {
              setBatchId(e.target.value);
              setStudentId('');
              setDirtyGuard(false);
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Choose a batch</MenuItem>
            {batches.map((batch) => (
              <MenuItem key={batch.batchId} value={batch.batchId}>
                {batch.batchName} ({batch.studentCount})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Student name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Type at least 2 letters"
            sx={{ minWidth: 220, flex: 1 }}
          />
        </Stack>
      </Paper>

      {listQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listQuery.error?.response?.data?.message || 'Could not load students.'}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <Paper sx={{ width: { md: 320 }, flexShrink: 0, maxHeight: { md: 640 }, overflow: 'auto' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2">
              Students {canList ? `(${students.length}${listQuery.data?.truncated ? '+' : ''})` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalStudents} attended in {monthLabel(month)}
            </Typography>
          </Box>
          {listQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !canList ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Choose a batch, or type a student name, to see the list.
            </Typography>
          ) : students.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No students match.
            </Typography>
          ) : (
            <List disablePadding>
              {students.map((student) => (
                <ListItemButton
                  key={student.studentId}
                  selected={student.studentId === studentId}
                  onClick={() => chooseStudent(student.studentId)}
                >
                  <ListItemText
                    primary={student.studentName}
                    secondary={`${(student.batches || []).join(', ')} · ${student.classCount} class${
                      student.classCount === 1 ? '' : 'es'
                    }`}
                  />
                  {student.saved ? <Chip size="small" color="success" label="Saved" /> : null}
                </ListItemButton>
              ))}
            </List>
          )}
          {listQuery.data?.truncated ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pb: 1.5 }}>
              Showing the first 100. Narrow the batch or name.
            </Typography>
          ) : null}
        </Paper>

        <Paper sx={{ p: 2, flex: 1, minHeight: 280 }}>
          {studentId ? (
            <StudentNoteEditor
              key={`${month}-${studentId}`}
              month={month}
              studentId={studentId}
              onDirtyChange={setDirtyGuard}
            />
          ) : (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Select a student to view and edit what they were taught.
            </Typography>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
